/**
 * Per-channel diagnostics for the audio-transcription pipeline.
 *
 * Two responsibilities:
 *
 *  1. `ChannelDiagAccumulator` — accumulates frame/byte/transcript
 *     counters in a windowed bucket plus lifetime totals. Owned by
 *     each AudioChannel; updated from the read loop, transport, and
 *     message handler.
 *
 *  2. `ChannelDiagnosticsReporter` — periodic timer that pulls a
 *     snapshot from every registered channel and prints a single
 *     consolidated log line per channel. Owned by the service.
 *
 * Why this matters: when Chromium hands us frames whose RMS reads fine
 * but Deepgram returns nothing, we can't tell from existing logs
 * whether (a) PCM is silently dropping, (b) one stereo plane is silent
 * / anti-correlated to the other, (c) frames contain NaN or clipping,
 * or (d) we're sending data but the server side is filtering it. The
 * diag block instruments enough of the pipeline to disambiguate.
 *
 * The most actionable failure mode this surfaces: cold-start system
 * audio that delivers frames with healthy RMS but produces zero
 * transcripts — we can now see whether bytesSent is climbing, whether
 * the WS is returning ANY messages, and whether the per-plane RMS
 * hints at AEC cancellation (one plane near-zero) or anti-correlation.
 */
import type { AudioPcmFrame } from './audio-pcm-pipeline'
import { GlobalState } from '@highlight/ipc'
import { isAudioDebugEnabled } from '@shared/audio-debug'
import { publishAudioDebugChannelState } from './audio-debug'
import { MEETING_MIC_GATE_KEY, NEUTRAL_MEETING_MIC_GATE, normalizeMeetingMicGate } from '@shared/types/meeting-mic-gate'
import { getMeetingParticipants } from '@renderer/services/meeting-participants-consumer'
import { TARGET_SAMPLE_RATE, type ChannelLabel } from './types'

/** How often the diagnostic summary line is emitted per channel. Long
 *  enough that the log isn't spammy, short enough to detect transient
 *  stalls. */
export const DIAG_INTERVAL_MS = 5000
/** Number of leading samples (Float32, then PCM16) to hex-dump on the
 *  first AudioData frame of each channel. Lets us eyeball whether the
 *  very first payload contains structured speech-shaped data,
 *  all-zeros, or constant values — the kind of thing that disambiguates
 *  "tap is silent" vs "tap is delivering data but it's nonsense". */
export const DIAG_FIRST_FRAME_SAMPLES = 16
/** Cap on how many per-frame timestamp/wall-clock detail lines we emit
 *  before falling back to the windowed summary. Five gives us enough to
 *  spot the startup transient (when buffers are still flushing through
 *  Chromium) and reach steady state, without flooding the log. */
export const DIAG_DETAILED_FRAMES = 5

/** Hex-dump the first N bytes of an ArrayBuffer in pairs (little-endian
 *  PCM16 presentation). Used by the first-frame diagnostic so we can
 *  compare a cold-start payload against a working-state payload
 *  byte-for-byte. */
export function hexDumpBytes(buffer: ArrayBuffer, maxBytes: number): string {
  const view = new Uint8Array(buffer, 0, Math.min(buffer.byteLength, maxBytes))
  return Array.from(view, (b) => b.toString(16).padStart(2, '0')).join(' ')
}

/** Format the leading PCM16 samples as signed integers for human reading.
 *  Scalar values are easier to spot-check for "looks like speech" than
 *  hex. */
export function pcm16Samples(buffer: ArrayBuffer, count: number): string {
  const view = new Int16Array(buffer, 0, Math.min(buffer.byteLength / 2, count))
  return Array.from(view).join(',')
}

interface DiagWindowBucket {
  framesInWindow: number
  bytesSentInWindow: number
  bytesBufferedInWindow: number
  rmsSumInWindow: number
  rmsMaxInWindow: number
  peakAbsInWindow: number
  nanSamplesInWindow: number
  clipSamplesInWindow: number
  wsMessagesInWindow: number
  transcriptsInWindow: number
  /** Total samples actually pulled from AudioData this window. Combined
   *  with the wall-clock window length on emit, gives us an EFFECTIVE
   *  sample rate per channel — exactly the signal needed to detect
   *  "track says 48kHz but is delivering at 24kHz" or "delivering on
   *  time but with gaps". */
  inputSamplesInWindow: number
  /** Sum and count of audioData.timestamp deltas for this window
   *  (microseconds). Average gives the average advance between frames
   *  in *audio time*. */
  timestampDeltaSumUs: number
  timestampDeltaCount: number
  timestampDeltaMinUs: number
  timestampDeltaMaxUs: number
  /** Sum and count of wall-clock deltas for this window (milliseconds). */
  wallDeltaSumMs: number
  wallDeltaCount: number
  wallDeltaMinMs: number
  wallDeltaMaxMs: number
}

interface DiagLifetimeTotals {
  framesReceived: number
  bytesSent: number
  transcripts: number
  wsMessages: number
}

/** Per-channel diagnostic accumulator. Public API is grouped by data source
 *  (frame, transport, transcript) so the surrounding orchestration code
 *  reads as a sequence of "tell the diag what just happened" calls. */
export class ChannelDiagAccumulator {
  /** Set on the first AudioData frame; used to one-shot a deep payload dump. */
  firstFrameLogged = false
  /** Number of detailed per-frame logs already emitted. Capped to keep
   *  noise low while still catching enough early frames to diff
   *  timestamp-vs-wall behaviour. */
  detailedFramesLogged = 0

  private window: DiagWindowBucket = createWindow()
  readonly totals: DiagLifetimeTotals = {
    framesReceived: 0,
    bytesSent: 0,
    transcripts: 0,
    wsMessages: 0,
  }

  /** Record a processed audio frame (called once per AudioData chunk). */
  recordFrame(frame: AudioPcmFrame): void {
    this.window.framesInWindow += 1
    this.totals.framesReceived += 1
    this.window.inputSamplesInWindow += frame.numFrames

    this.window.rmsSumInWindow += frame.energy
    if (frame.energy > this.window.rmsMaxInWindow) this.window.rmsMaxInWindow = frame.energy
    if (frame.peakAbs > this.window.peakAbsInWindow) this.window.peakAbsInWindow = frame.peakAbs
    this.window.nanSamplesInWindow += frame.nanCount
    this.window.clipSamplesInWindow += frame.clipCount

    if (frame.tsDeltaUs !== null && frame.tsDeltaUs > 0) {
      this.window.timestampDeltaSumUs += frame.tsDeltaUs
      this.window.timestampDeltaCount += 1
      if (frame.tsDeltaUs < this.window.timestampDeltaMinUs) this.window.timestampDeltaMinUs = frame.tsDeltaUs
      if (frame.tsDeltaUs > this.window.timestampDeltaMaxUs) this.window.timestampDeltaMaxUs = frame.tsDeltaUs
    }
    if (frame.wallDeltaMs !== null && frame.wallDeltaMs >= 0) {
      this.window.wallDeltaSumMs += frame.wallDeltaMs
      this.window.wallDeltaCount += 1
      if (frame.wallDeltaMs < this.window.wallDeltaMinMs) this.window.wallDeltaMinMs = frame.wallDeltaMs
      if (frame.wallDeltaMs > this.window.wallDeltaMaxMs) this.window.wallDeltaMaxMs = frame.wallDeltaMs
    }
  }

  /** Record bytes that left the WS for the wire. */
  recordBytesSent(bytes: number): void {
    this.window.bytesSentInWindow += bytes
    this.totals.bytesSent += bytes
  }

  /** Record bytes appended to the pre-WS / pre-server-ready buffer. */
  recordBytesBuffered(bytes: number): void {
    this.window.bytesBufferedInWindow += bytes
  }

  /** Record an inbound WS message (any type). */
  recordWsMessage(): void {
    this.window.wsMessagesInWindow += 1
    this.totals.wsMessages += 1
  }

  /** Record a transcript (final / transcription) message. */
  recordTranscript(): void {
    this.window.transcriptsInWindow += 1
    this.totals.transcripts += 1
  }

  /** Snapshot the current window for the reporter, then reset it for the
   *  next interval. Call from the reporter only — calling it elsewhere
   *  loses data. */
  snapshotAndReset(): DiagWindowBucket {
    const snapshot = this.window
    this.window = createWindow()
    return snapshot
  }
}

function createWindow(): DiagWindowBucket {
  return {
    framesInWindow: 0,
    bytesSentInWindow: 0,
    bytesBufferedInWindow: 0,
    rmsSumInWindow: 0,
    rmsMaxInWindow: 0,
    peakAbsInWindow: 0,
    nanSamplesInWindow: 0,
    clipSamplesInWindow: 0,
    wsMessagesInWindow: 0,
    transcriptsInWindow: 0,
    inputSamplesInWindow: 0,
    timestampDeltaSumUs: 0,
    timestampDeltaCount: 0,
    timestampDeltaMinUs: Infinity,
    timestampDeltaMaxUs: 0,
    wallDeltaSumMs: 0,
    wallDeltaCount: 0,
    wallDeltaMinMs: Infinity,
    wallDeltaMaxMs: 0,
  }
}

/** Snapshot supplied by the channel to the reporter on each tick. */
export interface ChannelDiagSnapshot {
  label: ChannelLabel
  diag: ChannelDiagAccumulator
  wsReadyState: number | null
  serverReady: boolean
  liveTrack: boolean
  trackInfo: string
  pcmBufferBytes: number
  /** Sample rate the OS reported for the RAW capture track (e.g.
   *  48000 for mic, ~24000-48000 for system loopback). PCM is always
   *  sent at TARGET_SAMPLE_RATE, but mic may reach AudioPcmPipeline at
   *  this raw rate because it bypasses Web Audio to preserve native AEC. */
  claimedSampleRate: number
}

/** Function called by the reporter to fetch the latest channel snapshot. */
export type SnapshotProvider = () => ChannelDiagSnapshot | null

/**
 * Periodic per-channel diagnostic reporter. Holds zero per-channel
 * state of its own — the source of truth lives on each AudioChannel,
 * which exposes a `snapshot()` provider. Channels can come and go
 * across restarts; the reporter keeps logging as long as `start()` was
 * called and the registered providers return non-null snapshots.
 */
export class ChannelDiagnosticsReporter {
  private timer: ReturnType<typeof setInterval> | null = null
  private startedAt = 0
  private providers: Array<SnapshotProvider> = []

  /** Register a snapshot provider. Adding the same provider twice is a
   *  no-op. */
  addProvider(provider: SnapshotProvider): void {
    if (!this.providers.includes(provider)) this.providers.push(provider)
  }

  /** Remove a snapshot provider. Idempotent. */
  removeProvider(provider: SnapshotProvider): void {
    this.providers = this.providers.filter((p) => p !== provider)
  }

  /** Begin the periodic reporter. Idempotent. */
  start(): void {
    this.stop()
    this.startedAt = Date.now()
    this.timer = setInterval(() => this.tick(), DIAG_INTERVAL_MS)
  }

  /** Stop the periodic reporter. Idempotent. */
  stop(): void {
    if (this.timer) {
      clearInterval(this.timer)
      this.timer = null
    }
  }

  /**
   * Force a tick immediately. Useful when the audio-debug overlay is
   * toggled on mid-session and we want the channel cards to populate
   * within ~1 frame instead of waiting up to `DIAG_INTERVAL_MS` for
   * the next scheduled tick. Performs the same window snapshot+reset
   * a normal tick does — the next scheduled tick simply has a shorter
   * window than usual, which only affects the cadence-style fields
   * (frames/s, KB/s) for one interval and self-corrects on the tick
   * after that.
   */
  forceTickNow(): void {
    this.tick()
  }

  private tick(): void {
    for (const provider of this.providers) {
      const snapshot = provider()
      if (!snapshot) continue
      this.emit(snapshot)
    }
  }

  private emit(snapshot: ChannelDiagSnapshot): void {
    const window = snapshot.diag.snapshotAndReset()
    const totals = snapshot.diag.totals
    const elapsedSec = ((Date.now() - this.startedAt) / 1000).toFixed(0)
    const wsState = snapshot.wsReadyState !== null ? (WS_STATE_NAMES[snapshot.wsReadyState] ?? 'UNKNOWN') : 'NULL'
    const meanRms = window.framesInWindow > 0 ? window.rmsSumInWindow / window.framesInWindow : 0
    const sentKB = (window.bytesSentInWindow / 1024).toFixed(1)
    const bufferedKB = (window.bytesBufferedInWindow / 1024).toFixed(1)
    const totalSentKB = (totals.bytesSent / 1024).toFixed(0)
    const tsAvgMs = window.timestampDeltaCount > 0 ? window.timestampDeltaSumUs / window.timestampDeltaCount / 1000 : 0
    const wallAvgMs = window.wallDeltaCount > 0 ? window.wallDeltaSumMs / window.wallDeltaCount : 0
    const tsRange =
      window.timestampDeltaCount > 0
        ? `${(window.timestampDeltaMinUs / 1000).toFixed(2)}-${(window.timestampDeltaMaxUs / 1000).toFixed(2)}`
        : 'n/a'
    const wallRange =
      window.wallDeltaCount > 0 ? `${window.wallDeltaMinMs.toFixed(2)}-${window.wallDeltaMaxMs.toFixed(2)}` : 'n/a'

    // Per-tick channel diagnostics are verbose (one line per channel every
    // DIAG_INTERVAL_MS) and drown out everything else while recording. Gate the
    // console line behind the audio-debug master flag (toggled by the audio
    // debug overlay, Cmd+Shift+D) so normal recordings stay quiet; the window
    // counters are still reset below and mirrored to the overlay regardless, so
    // enabling the flag shows the full diagnostics live without losing a tick.
    if (isAudioDebugEnabled()) {
      const meetingSummary = getMeetingDiagSummary()
      console.log(
        `[AudioTranscriptionService:${snapshot.label}] diag t+${elapsedSec}s ws=${wsState} ready=${snapshot.serverReady} live=${snapshot.liveTrack} track=${snapshot.trackInfo} | frames=${window.framesInWindow} samples=${window.inputSamplesInWindow} rawRate=${snapshot.claimedSampleRate}Hz tsΔ_avg=${tsAvgMs.toFixed(2)}ms tsΔ_range=${tsRange} wallΔ_avg=${wallAvgMs.toFixed(2)}ms wallΔ_range=${wallRange} | sentKB=${sentKB} bufferedKB=${bufferedKB} bufferedNow=${(snapshot.pcmBufferBytes / 1024).toFixed(1)}KB | rms_mean=${meanRms.toFixed(4)} rms_max=${window.rmsMaxInWindow.toFixed(4)} peakAbs=${window.peakAbsInWindow.toFixed(4)} nan=${window.nanSamplesInWindow} clip=${window.clipSamplesInWindow} | wsMsgs=${window.wsMessagesInWindow} transcripts=${window.transcriptsInWindow} | meeting ${meetingSummary} | totals frames=${totals.framesReceived} sent=${totalSentKB}KB transcripts=${totals.transcripts} wsMsgs=${totals.wsMessages}`,
      )
    }

    // Mirror the same tick snapshot to the overlay so UI and logs agree.
    publishAudioDebugChannelState({
      takenAtMs: Date.now(),
      label: snapshot.label,
      phase: snapshot.liveTrack ? 'acquired' : 'ended',
      claimedSampleRate: snapshot.claimedSampleRate,
      // Reserved for a future passive rate observer; overlay hides it at 0.
      measuredSampleRate: 0,
      targetSampleRate: TARGET_SAMPLE_RATE,
      framesPerSecond: window.framesInWindow / (DIAG_INTERVAL_MS / 1000),
      // Convert bytes/window → KB/s. Equivalent expression:
      //   (bytes / 1024) / (DIAG_INTERVAL_MS / 1000)
      bytesSentKbps: (window.bytesSentInWindow / 1024) * (1000 / DIAG_INTERVAL_MS),
      bytesSentTotal: totals.bytesSent,
      bufferedBytes: snapshot.pcmBufferBytes,
      transcriptsTotal: totals.transcripts,
      wsMessagesTotal: totals.wsMessages,
      rmsMean: meanRms,
      peakAbs: window.peakAbsInWindow,
      totalFrames: totals.framesReceived,
      wsReadyState: wsState as 'CONNECTING' | 'OPEN' | 'CLOSING' | 'CLOSED' | 'NULL',
      serverReady: snapshot.serverReady,
      liveTrack: snapshot.liveTrack,
      lastEvent: null,
    })
  }
}

const WS_STATE_NAMES = ['CONNECTING', 'OPEN', 'CLOSING', 'CLOSED'] as const

function getMeetingDiagSummary(): string {
  const gate = normalizeMeetingMicGate(GlobalState.get(MEETING_MIC_GATE_KEY, NEUTRAL_MEETING_MIC_GATE))
  const participants = getMeetingParticipants()
  const participantValues = Object.values(participants)
  const selfSpeaking = participantValues.some(
    (participant) => participant.isSelf === true && participant.isSpeaking === true,
  )
  const speakingCount = participantValues.filter((participant) => participant.isSpeaking === true).length
  const selfMute = gate.selfMute === null ? 'unknown' : gate.selfMute ? 'muted' : 'unmuted'

  return `participants=${participantValues.length} speaking=${speakingCount} selfSpeaking=${selfSpeaking} selfMute=${selfMute} merged=${gate.mergedAudio} captureWhileMuted=${gate.captureWhileMuted} dropMic=${gate.shouldDropMic}`
}
